import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareTheQR — a warm storybook "open the barn doors" QR card. Renders
 * a scannable, on-page card and invites visitors to share the page with
 * a friend using the browser's native share sheet (falling back to
 * clipboard copy). Printing / high-res PNG download lives exclusively
 * in the Admin QR Maker at /qr-maker so the public page stays inviting
 * and un-cluttered.
 */
const WINK_SRC = "/saltie-wink.png";
const SM_SYMBOL_SRC = "/assets/sm-circle-seal.png";
const ESPRESSO = "#4B3A2F";

const cornerStyle = (pos) => {
  const base = { position: "absolute", width: "clamp(3rem, 6vw, 4.5rem)", opacity: 0.35, pointerEvents: "none" };
  const px = "0.9rem";
  switch (pos) {
    case "tl": return { ...base, top: px, left: px, transform: "rotate(-14deg)" };
    case "tr": return { ...base, top: px, right: px, transform: "rotate(14deg) scaleX(-1)" };
    case "bl": return { ...base, bottom: px, left: px, transform: "rotate(-14deg)" };
    case "br": return { ...base, bottom: px, right: px, transform: "rotate(14deg) scaleX(-1)" };
    default: return base;
  }
};

const OPENED_MESSAGE = "\uD83E\uDE77 Another barn door just opened.";

export default function ShareTheQR({
  url = "https://thesaltymoo.com",
  label = "Open the Barn Doors",
  primaryCta = "Come Meet the Herd\u2026",
  secondaryCta = "Every Story Begins with One Saltie Wink\u2122",
}) {
  const [invited, setInvited] = useState(false);

  const handleInvite = async () => {
    // Always share the homepage so new visitors open the Barn Doors and
    // experience the storybook from the beginning — never a deep chapter link.
    const shareUrl = "https://thesaltymoo.com";
    const shareData = {
      title: "The Salty Moo\u2122",
      text: "Come meet the herd \u2014 a storybook you can wander through.",
      url: shareUrl,
    };
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share(shareData);
      } else if (
        typeof navigator !== "undefined" &&
        navigator.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Very old browsers: last-resort fallback.
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setInvited(true);
      toast(OPENED_MESSAGE, {
        description: "Thanks for sharing the pasture with a friend.",
      });
      setTimeout(() => setInvited(false), 6000);
    } catch (err) {
      // AbortError = user dismissed the share sheet; no toast needed.
      if (err && err.name !== "AbortError") {
        toast("Couldn\u2019t open the share sheet", {
          description: "Try copying the page link from your browser bar.",
        });
      }
    }
  };

  return (
    <div
      data-testid="share-the-qr"
      className="mt-20 max-w-2xl mx-auto text-center"
    >
      <div
        className="relative mx-auto p-5 sm:p-8 md:p-14 overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, #FBF1DB 0%, #F4E5C6 60%, #E8D9B4 100%)",
          // Deckled / worn paper edges via an irregular clip-path
          clipPath:
            "polygon(0.5% 1.2%, 2% 0.4%, 4% 1%, 7% 0.2%, 10% 0.9%, 14% 0.3%, 18% 1.1%, 23% 0.5%, 29% 0.7%, 35% 0.2%, 41% 0.9%, 48% 0.4%, 55% 1%, 62% 0.3%, 69% 0.8%, 76% 0.4%, 83% 1.1%, 89% 0.5%, 94% 0.7%, 98% 0.3%, 99.6% 2%, 99.2% 6%, 99.8% 12%, 99.3% 18%, 99.7% 26%, 99.2% 34%, 99.8% 42%, 99.3% 50%, 99.7% 58%, 99.2% 66%, 99.8% 74%, 99.3% 82%, 99.7% 90%, 99.2% 96%, 98% 99.4%, 94% 99.7%, 89% 99.3%, 83% 99.6%, 76% 99.2%, 69% 99.7%, 62% 99.3%, 55% 99.6%, 48% 99.2%, 41% 99.7%, 35% 99.3%, 29% 99.6%, 23% 99.2%, 18% 99.7%, 14% 99.3%, 10% 99.6%, 7% 99.2%, 4% 99.7%, 2% 99.3%, 0.5% 98%, 0.2% 94%, 0.7% 88%, 0.2% 80%, 0.8% 72%, 0.3% 64%, 0.7% 56%, 0.2% 48%, 0.8% 40%, 0.3% 32%, 0.7% 24%, 0.2% 16%, 0.6% 8%)",
          boxShadow:
            "0 22px 40px -18px rgba(75,58,47,0.45), inset 0 0 60px rgba(140,95,45,0.14)",
        }}
      >
        {/* Faint parchment grain texture */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-multiply"
          style={{
            backgroundImage:
              "radial-gradient(rgba(120,80,40,0.4) 1px, transparent 1.4px), radial-gradient(rgba(90,60,25,0.3) 0.8px, transparent 1.2px)",
            backgroundSize: "17px 17px, 9px 9px",
            backgroundPosition: "0 0, 4px 6px",
          }}
        />
        {/* Aged paper vignette */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 45%, rgba(90,60,25,0.22) 100%)",
          }}
        />
        {/* Faint embossed border — inner debossed line */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            inset: "1.4rem",
            borderRadius: "0.4rem",
            boxShadow:
              "inset 0 1px 0 rgba(255,241,214,0.7), inset 0 -1px 0 rgba(90,60,25,0.35), inset 1px 0 0 rgba(255,241,214,0.55), inset -1px 0 0 rgba(90,60,25,0.28)",
            border: "1px solid rgba(168,113,73,0.35)",
          }}
        />

        {/* Four Saltie Wink butterflies — one per corner */}
        <img src={WINK_SRC} alt="" aria-hidden="true" style={cornerStyle("tl")} />
        <img src={WINK_SRC} alt="" aria-hidden="true" style={cornerStyle("tr")} />
        <img src={WINK_SRC} alt="" aria-hidden="true" style={cornerStyle("bl")} />
        <img src={WINK_SRC} alt="" aria-hidden="true" style={cornerStyle("br")} />

        <div className="relative">
          {/* Heading in script */}
          <h2
            className="font-script text-[#4B3A2F] leading-tight"
            style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)" }}
            data-testid="share-qr-heading"
          >
            {label}
          </h2>
          <div className="gold-rule mx-auto my-3" style={{ width: "6rem" }} />

          {/* Centered QR — width caps at 200 px on phones, scales up on tablets/desktop. */}
          {/* Fixed-width wrapper so the SVG scales via CSS instead of overflowing. */}
          {/* box-sizing: content-box so the 200 px sizes the QR itself, not the padding. */}
          <div
            className="mx-auto bg-white rounded-sm shadow-sm"
            style={{
              boxSizing: "content-box",
              width: "min(200px, 60vw)",
              padding: "clamp(0.5rem, 1.4vw, 1rem)",
            }}
          >
            <QRCodeSVG
              id="share-qr-svg"
              value={url}
              // Rendered natively at 340 for print/download quality; CSS scales it
              // to fit the wrapper so mobile phones never see it overflow.
              size={340}
              level="H"
              bgColor="#FFFFFF"
              fgColor={ESPRESSO}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>

          {/* Salty Moo circle seal — transparent PNG, sits directly under the QR */}
          <img
            src={SM_SYMBOL_SRC}
            alt="The Salty Moo"
            className="mx-auto -mt-2 select-none pointer-events-none block"
            style={{ width: "clamp(12rem, 22vw, 17rem)" }}
            draggable="false"
          />

          {/* Two-line call to action */}
          <p
            className="font-script mt-2"
            style={{ color: ESPRESSO, fontSize: "clamp(1.4rem, 2.2vw, 2rem)", lineHeight: 1.15 }}
          >
            {primaryCta}
          </p>
          <p
            className="font-script mt-2"
            style={{ color: ESPRESSO, fontSize: "clamp(1rem, 1.5vw, 1.35rem)", lineHeight: 1.2 }}
          >
            {secondaryCta}
          </p>
        </div>
      </div>

      {/* Storybook "invitation" button — native share w/ clipboard fallback */}
      <button
        type="button"
        data-testid="share-qr-invite-btn"
        onClick={handleInvite}
        className="mt-10 inline-flex items-center gap-3 font-script transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A87149] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F0DE]"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, #EAD9B4 0%, #D8BE87 55%, #B18A4E 100%)",
          color: ESPRESSO,
          fontSize: "clamp(1.35rem, 2vw, 1.85rem)",
          padding: "0.75rem 1.85rem 0.85rem",
          borderRadius: "999px",
          border: "1px solid rgba(168,113,73,0.55)",
          boxShadow:
            "0 1px 0 rgba(255,241,214,0.7) inset, 0 -2px 0 rgba(75,58,47,0.28) inset, 0 12px 26px -12px rgba(75,58,47,0.55), 0 2px 0 rgba(75,58,47,0.22)",
          letterSpacing: "0.01em",
        }}
      >
        {invited ? (
          <>
            <Sparkles size={20} className="text-[#D62839]" />
            {OPENED_MESSAGE}
          </>
        ) : (
          <>
            <Share2 size={20} />
            Invite Someone to the Herd
          </>
        )}
      </button>

      <p className="text-xs italic text-[#6D5A50] mt-3">
        Send a friend through the barn doors &mdash; the pasture is always open.
      </p>
    </div>
  );
}
